import React, { useState, useMemo } from 'react';
import { 
  Box, useTheme, List, ListItemButton, ListItemIcon, ListItemText, 
  Collapse, TextField, InputAdornment, Grid, Card, CardActionArea, 
  CardContent, Typography, Divider, IconButton, Tooltip
} from '@mui/material';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

function ToolDashboard({ title, description, toolCategories, basePath }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: 4, height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        {description}
      </Typography>
      
      {toolCategories.map((cat, i) => (
        <Box key={i} sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom color="primary.main" fontWeight={600}>
            {cat.name}
          </Typography>
          <Grid container spacing={3}>
            {cat.tools.map((tool, j) => (
              <Grid item xs={12} sm={6} md={4} key={j}>
                <Card variant="outlined" sx={{ height: '100%', transition: '0.2s', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 2 } }}>
                  <CardActionArea onClick={() => navigate(tool.path.replace('/*', ''))} sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'primary.50', color: 'primary.main', display: 'flex', mr: 2 }}>
                          {tool.icon}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {tool.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {tool.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

function ToolAppLayout({ title, description, toolCategories, basePath }) {
  const theme = useTheme();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Flatten tools for routing
  const allTools = useMemo(() => toolCategories.flatMap(c => c.tools), [toolCategories]);
  
  // Determine which category to open initially based on current route
  const initialOpenState = useMemo(() => {
    const state = {};
    toolCategories.forEach((cat) => {
      // Set all categories expanded by default as requested
      state[cat.name] = true;
    });
    return state;
  }, [toolCategories]); 

  const [openCategories, setOpenCategories] = useState(initialOpenState);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleToggleCategory = (catName) => {
    setOpenCategories(prev => ({ ...prev, [catName]: !prev[catName] }));
  };

  // Filter categories and tools based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return toolCategories;
    const lowerQuery = searchQuery.toLowerCase();
    
    return toolCategories.map(cat => {
      const filteredTools = cat.tools.filter(t => 
        t.name.toLowerCase().includes(lowerQuery) || 
        t.description.toLowerCase().includes(lowerQuery)
      );
      return { ...cat, tools: filteredTools };
    }).filter(cat => cat.tools.length > 0);
  }, [searchQuery, toolCategories]);

  // If there's a search query, force all matching categories to be open
  const effectiveOpenCategories = useMemo(() => {
    if (searchQuery.trim()) {
      const state = {};
      filteredCategories.forEach(cat => state[cat.name] = true);
      return state;
    }
    return openCategories;
  }, [searchQuery, openCategories, filteredCategories]);

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: 'background.default', overflow: 'hidden' }}>
      {/* Vertical Sidebar */}
      <Box
        sx={{
          width: isSidebarOpen ? 240 : 0,
          borderRight: isSidebarOpen ? `1px solid ${theme.palette.divider}` : 'none',
          bgcolor: 'background.paper',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <Tooltip title="Close Sidebar">
            <IconButton size="small" onClick={() => setIsSidebarOpen(false)}>
              <MenuOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <List
          sx={{ flexGrow: 1, overflowY: 'auto', p: 0, '& .MuiListItemButton-root': { py: 1.5 } }}
          component="nav"
        >
          <ListItemButton 
            component={Link} 
            to="" 
            selected={location.pathname.endsWith(basePath) || location.pathname.endsWith(`${basePath}/`)}
            sx={{
               '&.Mui-selected': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.06)' }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}><DashboardIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }} />
          </ListItemButton>
          <Divider />
          
          {filteredCategories.map((cat, i) => (
            <React.Fragment key={cat.name}>
              <ListItemButton onClick={() => handleToggleCategory(cat.name)} sx={{ bgcolor: 'background.default', py: 1 }}>
                <ListItemText primary={cat.name} primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }} />
                {effectiveOpenCategories[cat.name] ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
              </ListItemButton>
              <Collapse in={effectiveOpenCategories[cat.name]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {cat.tools.map((tool) => {
                    const isActive = location.pathname.includes(tool.path.replace('/*', ''));
                    return (
                      <ListItemButton
                        key={tool.name}
                        component={Link}
                        to={tool.path.replace('/*', '')}
                        selected={isActive}
                        sx={{
                          pl: 4,
                          py: 1,
                          '&.Mui-selected': {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.06)',
                            borderRight: '3px solid #6366f1'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: isActive ? 'primary.main' : 'inherit' }}>
                          {tool.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={tool.name} 
                          primaryTypographyProps={{ 
                            fontSize: '0.875rem', 
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? 'primary.main' : 'text.primary'
                          }} 
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, height: '100%', overflow: 'hidden', position: 'relative' }}>
        {!isSidebarOpen && (
          <Tooltip title="Open Sidebar">
            <IconButton 
              onClick={() => setIsSidebarOpen(true)}
              sx={{ 
                position: 'absolute', 
                left: 16, 
                top: 16, 
                zIndex: 1000, 
                bgcolor: 'background.paper', 
                boxShadow: theme.shadows[2],
                '&:hover': { bgcolor: 'background.paper' }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}
        <Routes>
          <Route path="/" element={<ToolDashboard title={title} description={description} toolCategories={toolCategories} basePath={basePath} />} />
          {allTools.map(tool => {
            const isNested = tool.path.endsWith('/*');
            const path = isNested ? tool.path : tool.path;
            const Component = tool.component;
            return <Route key={tool.path} path={path} element={<Component />} />;
          })}
        </Routes>
      </Box>
    </Box>
  );
}

export default ToolAppLayout;
